import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Divider, Snackbar, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Order, mealService, Meal, Shift } from '../services';
import { useTranslation } from 'react-i18next';

const OrderSummary: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [meals, setMeals] = useState<{ [key: number]: Meal }>({});
  const [shifts, setShifts] = useState<{ [key: number]: Shift }>({});
  const [businessDays, setBusinessDays] = useState<{ date: string; dayName: string }[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const { t } = useTranslation();

  const fetchOrders = async () => {
    try {
      const [ordersData, nextBusinessDays, shiftsData] = await Promise.all([
        mealService.getOrders(),
        mealService.getNextBusinessDays(5),
        mealService.getShifts()
      ]);

      // Fetch meals data
      const mealPromises = ordersData.map(order => mealService.getMealById(order.mealId));
      const mealsData = await Promise.all(mealPromises);
      
      const mealsMap: { [key: number]: Meal } = {};
      mealsData.forEach((meal, index) => {
        if (meal) {
          mealsMap[ordersData[index].mealId] = meal;
        }
      });
      
      // Create shifts map for easy lookup
      const shiftsMap: { [key: number]: Shift } = {};
      shiftsData.forEach(shift => {
        shiftsMap[shift.id] = shift;
      });

      setOrders(ordersData);
      setMeals(mealsMap);
      setShifts(shiftsMap);
      setBusinessDays(nextBusinessDays);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setSnackbar({ open: true, message: 'Failed to load orders. Please try again later.' });
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const getColorForMeal = (mealId: number): string => {
    const colors = ['#FFB3BA', '#BAFFC9', '#BAE1FF', '#FFFFBA', '#FFD8BA'];
    return colors[mealId % colors.length];
  };

  const handleDeleteOrder = async (orderId: number) => {
    try {
      await mealService.deletePreOrder(orderId);
      setSnackbar({ open: true, message: t('preorderdeletesuccess') });
      fetchOrders(); // Refresh the orders after deletion
    } catch (error) {
      console.error('Failed to delete pre-order:', error);
      setSnackbar({ open: true, message: 'Failed to delete pre-order. Please try again.' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('YourPreOrders')}
      </Typography>
      {businessDays.map((day) => {
        const order = orders.find(o => o?.deliveryDate?.startsWith(day.date));
        const meal = order ? meals[order.mealId] : null;

        return (
          <Paper key={day.date} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: order ? getColorForMeal(order.mealId) : 'grey',
                  }}
                />
                <Typography variant="h6">{`${t(day.dayName)}, ${day.date}`}</Typography>
              </Box>
              {order && (
                <IconButton onClick={() => handleDeleteOrder(order.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
            {order && meal ? (
              <>
                <Typography variant="body1">{meal.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('price')}: {meal.price.toFixed(2)}
                </Typography>
                {order.shiftId && shifts[order.shiftId] && (
                  <Typography variant="body2" color="text.secondary">
                    {t('selectShift')}: {shifts[order.shiftId].name} - {
                      typeof shifts[order.shiftId].orderDeliveryTime === 'string'
                        ? new Date(`2000-01-01T${shifts[order.shiftId].orderDeliveryTime}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                        : new Date(shifts[order.shiftId].orderDeliveryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                    }
                  </Typography>
                )}
                {order.specialRequest && (
                  <Typography variant="body2" color="text.secondary">
                    {t('specialRequest')}: {order.specialRequest}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                {t('noMealOrdered')}
              </Typography>
            )}
          </Paper>
        );
      })}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default OrderSummary;